<template>
  <v-menu
    offset-y allow-overflow max-height="80%" min-width="240"
    :close-on-click="false"
    :close-on-content-click="false"
    v-model="open"
  >
    <error-dialog :status="addTagStatus">
      Error adding tag – note that you cannot add multi-word tags unless they match an existing category
    </error-dialog>
    <error-dialog :status="removeTagStatus" />
    <error-dialog :status="updateTagScoreStatus" />

    <template v-slot:activator="{ on, attrs, value }">
      <slot v-bind="{ on, attrs }">
        <v-btn
          v-on="on" v-blur
          icon
          :input-value="value"
          :color="value ? 'orange' : null"
        >
          <v-icon v-html="mdiTag" />
        </v-btn>
      </slot>
    </template>
    <div class="white">
      <div class="px-2 overline">Tags</div>
      <div
        v-for="[ tagId, tag ] in tags" :key="tagId"
        v-if="(isAdmin || tag.tag) && ![ 'new', 'free', 'template' ].includes(tag.stem)"
        class="d-flex align-center px-2"
        v-bind:class="{ blue: tagId == duplicateTagId }"
      >
        <div
          v-if="tag.tag || isAdmin"
          class="flex-grow-1 text--label-active"
          v-bind:class="{ 'font-italic grey--text': !tag.tag }"
        >
          {{tag.tag || 'Overall score'}}
        </div>
        <v-text-field
          v-if="isAdmin"
          class="flex-grow-0 my-0"
          style="width: 34px;"
          dense hide-details
          v-model="tag._score"
          :color="tag._score == tag.score ? null : 'amber'"
          :error="Number(tag._score) != tag._score || tag._score <= 0 || tag._score > 1"
          :loading="updateTagScoreStatus.pending"
          @keydown.enter="updateTagScore(tagId, tag)"
          @blur="tag._score = tag.score"
        />
        <v-btn
          class="ml-1"
          icon
          :disabled="!tag.tag || (!isAdmin && tag.tag.startsWith('#'))"
          @click="removeTag(tagId)"
        >
          <v-icon v-html="mdiClose" />
        </v-btn>
      </div>
      <div v-if="isAdmin || !readonly" class="px-2 pb-2 d-flex align-center">
        <v-combobox
          ref="new-tag"
          dense hide-details autofocus class="mt-0"
          :items="categoryNames"
          :loading="loadCategoryNamesStatus.pending || addTagStatus.pending"
          :disabled="addTagStatus.pending"
          :search-input.sync="newTagInput"
          v-model="newTag"
          @change="addTag"
        />
        <v-btn class="ml-1" icon color="primary" :disabled="!newTagInput" @click="addTag">
          <v-icon v-html="mdiPlus" />
        </v-btn>
      </div>
    </div>
  </v-menu>
</template>


<script>
import { cleanTag } from '@/utils/tags.js';
import { mdiTag, mdiClose, mdiPlus } from '@mdi/js';


function sortTags(tags) {
  tags.sort((a, b) => {
    if (!a[1].tag)
      return -1;
    if (!b[1].tag)
      return 1;
    return (b[1].score - a[1].score) || a[1].tag.localeCompare(b[1].tag);
  });
}


export default {
  name: 'tag-editor',

  props: [ 'scope', 'targetId', 'readonly' ],

  data() {
    return {
      open: false,
      categoryNames: [],
      newTag: null,
      newTagInput: '',
      tags: [],
      duplicateTagId: null,
      loadCategoryNamesStatus: this.$track('loadCategoryNames'),
      addTagStatus: this.$track('addTag'),
      removeTagStatus: this.$track('removeTag'),
      updateTagScoreStatus: this.$track('updateTagScore')
    };
  },

  computed: {
    isAdmin() {
      return this.global.profile && this.global.profile.admin;
    },

    categoryNamesCleaned() {
      return this.categoryNames.map(cleanTag);
    }
  },

  created() {
    Object.assign(this, { mdiTag, mdiClose, mdiPlus });
  },

  methods: {
    async loadCategoryNames() {
      this.categoryNames = [
        ...(this.global.profile?.admin ? [ '#design-your-own', '#page-template' ] : []),
        ...(await this.$call('list-category-tags')).map(x => x.toLowerCase())
      ];
    },

    async loadTags() {
      const tags = await this.$call('list-tags', { target_id: this.targetId, scope: this.scope });
      for (const [ _, tag ] of tags)
        tag._score = tag.score;
      sortTags(tags);
      this.tags = tags;
      this.$emit('loaded-tags', tags);
    },

    async updateTagScore(tag_id, tag) {
      await this.$call('update-tag-score', { tag_id, score: tag._score, scope: this.scope });
      tag.score = tag._score;
    },

    async addTag(tag) {
      if (!tag)
        return;

      const cleaned = cleanTag(tag);

      // You can only add multi-word tags if they correspond to a category name
      if ((cleaned.indexOf(' ') != -1) && (this.categoryNamesCleaned.indexOf(cleaned) == -1))
        throw 'Multi-word tag';

      const response = await this.$call('add-tag', { target_id: this.targetId, tag: cleaned, scope: this.scope });
      if (response.is_duplicate) {
        this.duplicateTagId = response.tag_id;
        setTimeout(() => this.duplicateTagId = null, 5000);
      } else {
        response.tag._score = response.tag.score;
        this.duplicateTagId = null;
        const tags = this.tags.concat([ [ response.tag_id, response.tag ] ]);
        sortTags(tags);
        this.tags = tags;
      }
      await this.$nextTick();
      this.newTag = null;
      this.newTagInput = '';
      setTimeout(() => this.$refs['new-tag'].$el.querySelector('input').focus());
    },

    async removeTag(tag_id) {
      await this.$call('remove-tag', { tag_id, scope: this.scope });
      this.tags = this.tags.filter(t => t[0] != tag_id);
    }
  },

  watch: {
    open(v) {
      if (v && !this.readonly) {
        this.loadCategoryNames();
        this.loadTags();
      }
    }
  }
};
</script>
